import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeaderSection } from "../../../components/common/PageHeadSection";
import { Input, Skeleton, Space, Spin } from "antd";
import { HotelSelect } from "../../../components/common/HotelSelect";
import { useMutation, useQuery } from "react-query";
import { mdlBoardListRequest } from "../../../models/service-models/board/BoardListRequest";
import { UserService } from "../../../services/UserService";
import { UserTable } from "../../../components/user/UserTable";
import { mdlUserDeleteRequest } from "../../../models/service-models/user/UserDeleteRequest";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../../utils/ObjectHelper";
import { SFModal } from "../../../components/elements/SFModal";
import UserPermissions from "../../../components/user/UserPermissions";
import { UserCopy } from "../../../components/user/UserCopy";
import UserDetail from "../../../components/user/UserDetail";
import { mdlUser } from "../../../models/domain/User";
import { mdlTablePagination } from "../../../models/ui-models/TablePagination";
import _ from 'lodash'

export const UserDefinition = () => {
    const { t } = useTranslation();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlBoardListRequest>({ pageNumber: 1, pageSize: 10 });
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<mdlUser>();
    const [selectedUserName, setSelectedUserName] = useState<string>();
    const [openCopyModal, setOpenCopyModal] = useState<boolean>(false);
    const [openPermissionModal, setOpenPermissionModal] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");

    useEffect(() => {
        if (!data && selectedHotel)
            refetch();
    }, [selectedHotel, request]);


    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getUsers", selectedHotel, request, search],
        () => {
            if (selectedHotel)
                return UserService.List({ ...request, hotelId: selectedHotel, name: search });
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );

    const handleHotelSelect = (hotelId: string) => {
        setSelectedHotel(hotelId);
    }
    const handleAddNew = () => {
        setSelectedUser(undefined);
        setOpenDetailModal(true);
    }
    const handleDelete = (userId: string) => {
        var user = data?.body?.data?.find(user => user.id == userId);
        if (user != null && selectedHotel != null)
            deleteUser({ userName: user?.userName!, hotelId: selectedHotel });
    }
    const handleCopy = (userId: string) => {
        var user = data?.body?.data?.find(user => user.id == userId);
        if (user) {
            setSelectedUserName(user.userName)
            setOpenCopyModal(true)

        }
    }
    const handlePermission = (userId: string) => {
        var user = data?.body?.data?.find(user => user.id == userId);
        setSelectedUser(user)
        setOpenPermissionModal(true)
    }
    const handleEdit = (userId: string) => {
        var user = data?.body?.data?.find(user => user.id == userId);
        setSelectedUser(user)
        setOpenDetailModal(true)
    }
    const { mutateAsync: deleteUser, isLoading: mutateLoading, data: mutateData } = useMutation(
        (request: mdlUserDeleteRequest) => UserService.Delete(request),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                    refetch();
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handlePermissionCallback = () => {
        setOpenPermissionModal(false);
        refetch();
    }
    const handleDetailCallback = () => {
        setOpenDetailModal(false);
        setSelectedUser(undefined)
        refetch();
    }
    const handleCopyCallback = () => {
        setOpenCopyModal(false);
        refetch();
    }
    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearch(e.target.value);
    };
    const debouncedSearch = useCallback(
        _.debounce((value: string) => setSearch(value), 500),
        []
    );
    return (
        <Spin spinning={mutateLoading}>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("definitions.users.title")}
                                addNewFunction={selectedHotel ? handleAddNew : undefined}
                            />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">

                                                <HotelSelect onChange={handleHotelSelect} />
                                                {selectedHotel && (
                                                    <>
                                                        <hr />
                                                        <Space align="center" style={{ justifyContent: "flex-end", display: "flex" }}>
                                                            <Input.Search
                                                                placeholder={t("definitions.users.userName")}
                                                                onChange={handleSearchChange}
                                                            />
                                                        </Space>
                                                        <hr />
                                                    </>
                                                )}

                                                {
                                                    isLoading
                                                    &&
                                                    <>
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                    </>
                                                }
                                                {
                                                    data
                                                    &&
                                                    <UserTable
                                                        onDelete={handleDelete}
                                                        onCopy={handleCopy}
                                                        onPermission={handlePermission}
                                                        onEdit={handleEdit}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.resultCount} ` + t("pagination.result"),
                                                            locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                        }}
                                                        onTableParamsChange={handleTableParamsChange}
                                                        dataSource={data?.body?.data} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openPermissionModal}
                toggler={() => { setOpenPermissionModal(!openPermissionModal); setSelectedUser(undefined) }}
                body={<UserPermissions userId={selectedUser?.userId} hotelId={selectedHotel} callback={handlePermissionCallback} />}
                title={t("definitions.users.updatePermissionsTitle")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openCopyModal}
                toggler={() => { setOpenCopyModal(!openCopyModal); setSelectedUserName(undefined) }}
                body={<UserCopy hotelId={selectedHotel} userName={selectedUserName} callback={handleCopyCallback} />}
                title={t("definitions.users.copyPermissions")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openDetailModal}
                toggler={() => { setOpenDetailModal(!openDetailModal); setSelectedUser(undefined) }}
                body={<UserDetail hotelId={selectedHotel} userId={selectedUser?.userId} callback={handleDetailCallback} />}
                title={t("definitions.users.detailTitle")}
            />
        </Spin>
    );
};
